import React, { useMemo, useRef } from 'react';
import Plot from 'react-plotly.js';
import {
  adaptiveSamplingTheme,
  basePlotlyLayout,
  paletteToColorscale,
} from './theme';
import { FullscreenButton } from './FullscreenButton';

export type HeatmapPanelProps = {
  x: number[];
  y: number[];
  z: number[][];
  uncertainty?: number[][];
  xLabel: string;
  yLabel: string;
  zLabel: string;
  /**
   * When set to `'ridge'`, a contour line is drawn along the max-z ridge in
   * the adaptive-sampling pink highlight. Default `'none'`.
   */
  highlight?: 'ridge' | 'none';
  onClick?: (x: number, y: number) => void;
  fullscreen?: boolean;
};

/**
 * For every column of x, find the y that maximises z. Returns a ridge polyline
 * suitable for an overlay contour / scatter.
 */
function computeRidge(
  x: number[],
  y: number[],
  z: number[][],
): { x: number[]; y: number[] } {
  const rx: number[] = [];
  const ry: number[] = [];
  if (!z.length || !x.length || !y.length) return { x: rx, y: ry };
  for (let i = 0; i < x.length; i++) {
    let bestJ = 0;
    let bestZ = -Infinity;
    for (let j = 0; j < y.length; j++) {
      const row = z[j];
      if (!row) continue;
      const v = row[i];
      if (v === undefined || v === null || Number.isNaN(v)) continue;
      if (v > bestZ) {
        bestZ = v;
        bestJ = j;
      }
    }
    const xi = x[i];
    const yj = y[bestJ];
    if (xi !== undefined && yj !== undefined && Number.isFinite(bestZ)) {
      rx.push(xi);
      ry.push(yj);
    }
  }
  return { x: rx, y: ry };
}

export const HeatmapPanel: React.FC<HeatmapPanelProps> = ({
  x,
  y,
  z,
  uncertainty,
  xLabel,
  yLabel,
  zLabel,
  highlight = 'none',
  onClick,
  fullscreen = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const data = useMemo(() => {
    const t = adaptiveSamplingTheme;
    const traces: Record<string, unknown>[] = [
      {
        type: 'heatmap',
        x,
        y,
        z,
        colorscale: paletteToColorscale(t.sequentialPalette),
        colorbar: {
          title: { text: zLabel, font: { color: t.textMuted, size: 11 } },
          tickfont: { color: t.textMuted, size: 10 },
          outlinewidth: 0,
          thickness: 10,
        },
        hovertemplate:
          `${xLabel}: %{x:.3g}<br>${yLabel}: %{y:.3g}<br>${zLabel}: %{z:.3g}<extra></extra>`,
      },
    ];

    if (uncertainty) {
      traces.push({
        type: 'contour',
        x,
        y,
        z: uncertainty,
        showscale: false,
        contours: { coloring: 'lines', showlabels: false },
        line: { color: 'rgba(255,255,255,0.18)', width: 1 },
        hoverinfo: 'skip',
        opacity: 0.6,
      });
    }

    if (highlight === 'ridge') {
      const ridge = computeRidge(x, y, z);
      traces.push({
        type: 'scatter',
        mode: 'lines',
        x: ridge.x,
        y: ridge.y,
        line: { color: t.pink, width: 2.5, shape: 'spline' },
        showlegend: false,
        hoverinfo: 'skip',
        name: 'ridge',
      });
    }

    return traces;
  }, [x, y, z, uncertainty, xLabel, yLabel, zLabel, highlight]);

  const layout = useMemo(
    () =>
      basePlotlyLayout({
        xaxis: {
          ...(basePlotlyLayout().xaxis as object),
          title: { text: xLabel },
        },
        yaxis: {
          ...(basePlotlyLayout().yaxis as object),
          title: { text: yLabel },
        },
        autosize: true,
        height: fullscreen ? undefined : 420,
      }),
    [xLabel, yLabel, fullscreen],
  );

  const config = useMemo(
    () => ({
      displayModeBar: false,
      responsive: true,
    }),
    [],
  );

  return (
    <div
      ref={containerRef}
      className={`as-chart-wrap${fullscreen ? ' as-fullscreen' : ''}`}
    >
      <div className="as-chart-fs-slot">
        <FullscreenButton targetRef={containerRef as React.RefObject<HTMLElement>} />
      </div>
      <Plot
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data={data as any}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        layout={layout as any}
        config={config}
        useResizeHandler
        style={{ width: '100%', height: '100%' }}
        onClick={(ev) => {
          if (!onClick) return;
          const p = ev?.points?.[0];
          if (p && typeof p.x === 'number' && typeof p.y === 'number') {
            onClick(p.x, p.y);
          }
        }}
      />
    </div>
  );
};
